import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Pencil } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

export default function Stages() {
  const [stages, setStages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', order: 1, active: true });
  const [loading, setLoading] = useState(true);

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => { api.get('/stages').then(r => setStages(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleSave = async () => {
    if (!form.name) { 
      setErrorModal({
        open: true,
        title: 'Campo requerido faltante',
        message: 'Por favor, ingresa el nombre de la etapa.'
      });
      return; 
    }
    try {
      if (editing) { await api.put(`/stages/${editing}`, form); toast.success('Etapa actualizada'); }
      else { await api.post('/stages', form); toast.success('Etapa creada'); }
      setShowForm(false); setEditing(null); setForm({ name: '', order: 1, active: true }); load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || 'Error al guardar etapa';
      setErrorModal({
        open: true,
        title: editing ? 'Error al actualizar etapa' : 'Error al crear etapa',
        message: errorMessage
      });
    }
  };

  const handleEdit = (s) => { setEditing(s.id); setForm({ name: s.name, order: s.order, active: s.active }); setShowForm(true); };
  const handleDelete = async (id) => { if (!window.confirm('Eliminar etapa?')) return; await api.delete(`/stages/${id}`); toast.success('Etapa eliminada'); load(); };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-stages-page">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Etapas del Torneo</h2>
        <button onClick={() => { setEditing(null); setForm({ name: '', order: stages.length + 1, active: true }); setShowForm(true); }} className="gold-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="create-stage-button">
          <Plus size={16} /> Crear
        </button>
      </div>

      {loading ? <div className="text-center text-white/50 py-10">Cargando...</div> : stages.length === 0 ? (
        <div className="glass-card p-8 text-center text-white/40">No hay etapas registradas</div>
      ) : (
        <div className="space-y-2">
          {stages.map(s => (
            <div key={s.id} className="glass-card p-4 flex items-center justify-between" data-testid={`stage-row-${s.order}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#F4E39E]/20 flex items-center justify-center text-[#F4E39E] font-bold text-sm">{s.order}</div>
                <div>
                  <div className="text-white font-semibold text-sm">{s.name}</div>
                  <Badge className={s.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                    {s.active ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(s)} className="p-2 rounded-lg hover:bg-white/10 text-white/50 hover:text-[#F4E39E]"><Pencil size={16} /></button>
                <button onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-[#F4E39E]" style={{ fontFamily: 'Montserrat' }}>{editing ? 'Editar Etapa' : 'Crear Etapa'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">Nombre de la etapa</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="stage-name-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" placeholder="Fase de Grupos 1" />
            </div>
            <div>
              <label className="text-white/60 text-sm">Orden</label>
              <input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 1 })} data-testid="stage-order-input"
                className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-4 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-white/60 text-sm">Activa</label>
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })}
                className="w-5 h-5 accent-[#F4E39E]" />
            </div>
            <button onClick={handleSave} className="gold-btn w-full py-3 rounded-lg font-bold" data-testid="save-stage-button">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
